/**
 * Edge-highlight rim util — a reusable "rim light" for dark silhouettes. Given a
 * closed outline, produces a thin bright stroke so the shape's edge catches
 * light (the "bones read as bones" problem). Composes with `bakeGraphicsToSprite`.
 *
 * Two parts:
 *   - `edgeHighlightAlpha` — pure LFO sampler for the rim alpha (testable).
 *   - `drawEdgeHighlight` — strokes the outline into a Graphics (render-side).
 *
 * @see docs/design/systems/edge-highlight-util.md — blueprint.
 */

/** Configurable rim-stroke spec. Colors are LIT (additive-friendly). */
export interface EdgeHighlightSpec {
  /** Rim tint (cold/bright — LIT for additive blend). */
  color: number;
  /** Stroke width in px. */
  widthPx: number;
  /** Alpha LFO range (set min==max for a static rim). */
  alphaMin: number;
  alphaMax: number;
  /** LFO period in ms (omit or 0 = static at alphaMax). */
  periodMs?: number;
}

/**
 * Sample the rim alpha at `elapsedMs`. When no period is set (or 0), returns
 * `alphaMax` (static). Otherwise cosine-eases between min and max — gentle, no
 * strobing. Pure (no rendering deps) so it's fully unit-testable.
 */
export function edgeHighlightAlpha(
  spec: EdgeHighlightSpec,
  elapsedMs: number,
  seed = 0,
): number {
  if (!spec.periodMs || spec.periodMs <= 0) return spec.alphaMax;
  const phase = (seed * 0.7) % 1; // seeded phase offset so multiple rims don't sync
  const t = ((elapsedMs / spec.periodMs) + phase) % 1;
  const eased = 0.5 - 0.5 * Math.cos(t * Math.PI * 2);
  return spec.alphaMin + (spec.alphaMax - spec.alphaMin) * eased;
}
